import React, { useState, useEffect } from 'react';
import { Prediction, CustomerUploadResponse } from '../../types/CustomerUpload';
import LocatorDropdownProps from "../../components/DropdownProps/LocatorDropdownProps";
import Modal from "../../components/Modal";
import { formatDate } from "../../utils/formatDate";
import { api } from '../../services/Api';
import errorOptions from '../../pages/Evaluate/ErrorOptions';
import './Evaluate.scss';
import '../../components/Modal/Modal.scss';

const PredictionDetailsCard: React.FC<{
  prediction: Prediction;
  index: number;
  isFromIndex: boolean;
  isToIndex: boolean;
  onSelect: () => void;
}> = ({ prediction, index, isFromIndex, isToIndex, onSelect }) => {
  const getSelectionClass = () => {
    if (isFromIndex && isToIndex) return 'selected-to'; // Green when it's both
    if (isFromIndex) return 'selected-from'; // Orange when it's from
    if (isToIndex) return 'selected-to'; // Green when it's to
    return '';
  };

  return (
    <div 
      className={`prediction-details ${getSelectionClass()}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <div className="prediction-header">
        <span className="prediction-number">Prediction {index + 1}</span>
        {(isFromIndex || isToIndex) && (
          <span className={`selection-type ${isFromIndex && isToIndex ? 'hidden' : ''}`}>
            {isFromIndex && isToIndex ? '' : isFromIndex ? 'Current' : 'New'}
          </span>
        )}
      </div>
      <div className="prediction-content">
        <div className="prediction-item">
          <div className="label">Confidence</div>
          <div className="value">{prediction.confidence.toFixed(2)}%</div>
        </div>
        <div className="prediction-item">
          <div className="label">Details</div>
          <div className="value">{prediction.typename}</div>
        </div>
        <div className="prediction-item">
          <div className="label">Locator Length (mm)</div>
          <div className="value">{prediction.length}</div>
        </div>
      </div>
    </div>
  );
};

const PredictionCard: React.FC<{
  prediction: CustomerUploadResponse;
  onAccept: (prediction: CustomerUploadResponse, selectedIndex: number) => void;
  onChange: (prediction: CustomerUploadResponse, fromIndex: number, toIndex: number) => void;
  onError: (prediction: CustomerUploadResponse) => void;
  onCustomChange: (prediction: CustomerUploadResponse, toIndex: number) => void;
}> = ({ prediction, onAccept, onChange, onError, onCustomChange }) => {
  const defaultSelectedIndex = prediction.predictions.findIndex(p => p.isSelected);
  const initialIndex = defaultSelectedIndex >= 0 ? defaultSelectedIndex : 0;

  const [fromIndex, ] = useState<number>(initialIndex);
  const [toIndex, setToIndex] = useState<number>(initialIndex);

  const handlePredictionSelect = (index: number) => {
    setToIndex(index === fromIndex ? fromIndex : index);
  };

  const isAcceptDisabled = (fromIndex !== toIndex) || (prediction.predictions.length === 0);
  
  const hasChangedVerifyMessage = prediction.verifymessage && prediction.verifymessage.toLowerCase().includes("to custom");

  return (
    <div className="prediction-card">
      <div className="card-header">
        <div className="customer-info">

          <div>
            <span className="label">Customer:</span>
            <span className="value">{prediction.clientname}</span>
          </div>
          <div>
            <span className="label">Uploaded:</span>
            <span className="value">{formatDate(prediction.dateuploaded)}</span>
          </div>
          {
            prediction.dateverified && <div>
              <span className="label">Verified:</span>
              <span className="value">{formatDate(prediction.dateverified)} by {prediction.verifieremail}
              </span>
            </div>
          }
        </div>
        <div className="action-buttons">
            {prediction.predictions.length > 0 && (
            <button
              className={`accept ${isAcceptDisabled ? 'disabled' : ''}`}
              onClick={() => onAccept(prediction, fromIndex)}
              disabled={isAcceptDisabled}
            >
              Accept
            </button>
            )}
            <button
            className="custom-change"
            onClick={() => onCustomChange(prediction, toIndex)}
            >
            {prediction.predictions.length === 0 ? 'Custom' : 'Change'}
            </button>
          <button className="error" onClick={() => onError(prediction)}>
            Error
          </button>
        </div>
      </div>

      <div className="card-content">
        <div className="image-container">
          <img src={prediction.path} alt="Part" />
        </div>
        {prediction.status === 'PREDICTION_FAILED' ? (
          <div className="error-message">
            <p>Something went wrong with this prediction. <br />
              The model could not return any predictions.</p>
          </div>
        ) : prediction.status === 'UPLOADED' ? (
          <div className="error-message">
            <p>Please wait until the model has made a prediction.</p>
          </div>
        ) : hasChangedVerifyMessage ? (
          <div className="verify-message">
            <p>{prediction.verifymessage}</p>
          </div>
        ) : prediction.status === 'FAILED' ? (
          <div className="verify-message">
            <p>{prediction.verifymessage}</p>
          </div>
        ) : (
          <div className="predictions-container">
            {prediction.predictions.map((pred, idx) => (
              <PredictionDetailsCard
          key={idx}
          prediction={pred}
          index={idx}
          isFromIndex={idx === fromIndex}
          isToIndex={idx === toIndex}
          onSelect={() => handlePredictionSelect(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const EvaluateLocators: React.FC = () => {
  const [predictions, setPredictions] = useState<CustomerUploadResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(true);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'accept' | 'change' | 'error' | 'custom-change' | null;
    prediction: CustomerUploadResponse | null;
    fromIndex: number;
    toIndex: number;
  }>({
    isOpen: false,
    type: null,
    prediction: null,
    fromIndex: 0,
    toIndex: 0
  });

  const [customValues, setCustomValues] = useState<{
    typename: string;
    typeid: string;
    length: number;
    lengthid: string;
  }>({
    typename: '',
    typeid: '',
    length: 0,
    lengthid: ''
  });

  const [selectedError, setSelectedError] = useState<string>('');
  const [customError, setCustomError] = useState<string>('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/client-uploads?model_type=locator');
      setPredictions(response);
      setError(null);
    } catch (err) {
      console.log(err);
      setError(err instanceof Error ? err.message : 'Failed to fetch predictions');
    } finally {
      setLoading(false);
    }
  };

  const filteredPredictions = predictions.filter(pred =>
    !showUnverifiedOnly || pred.dateverified === null
  );

  const handleUpdatePrediction = async (
    clientuploadid: string,
    status: string,
    verifiedtypeid?: string,
    verifiedlengthid?: string,
    verifymessage?: string
  ) => {
    try {
      await api.put(`/client-uploads/${clientuploadid}`, {
        status,
        verifiedtypeid,
        verifiedlengthid,
        verifymessage,
      });
      await fetchPredictions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prediction');
    }
  };

  const handleAcceptClick = (prediction: CustomerUploadResponse, selectedIndex: number) => {
    setModalState({
      isOpen: true,
      type: 'accept',
      prediction,
      fromIndex: selectedIndex,
      toIndex: selectedIndex
    });
  };

  const handleCustomChangeClick = (prediction: CustomerUploadResponse, toIndex: number) => {
    const selectedPrediction = prediction.predictions[toIndex];
    setCustomValues({
      typename: selectedPrediction? selectedPrediction.typename : '',
      typeid: selectedPrediction ? selectedPrediction.typeid : '',
      length: selectedPrediction ? selectedPrediction.length : 0,
      lengthid: selectedPrediction ? selectedPrediction.lengthid : ''
    });
    setModalState({
      isOpen: true,
      type: 'custom-change',
      prediction,
      fromIndex: toIndex,
      toIndex: toIndex
    });
  };

  const handleChangeClick = (prediction: CustomerUploadResponse, fromIndex: number, toIndex: number) => {
    setModalState({
      isOpen: true,
      type: 'change',
      prediction,
      fromIndex,
      toIndex
    });
  };

  const handleErrorClick = (prediction: CustomerUploadResponse) => {
    setModalState({
      isOpen: true,
      type: 'error',
      prediction,
      fromIndex: 0,
      toIndex: 0
    });
    setSelectedError('');
    setCustomError('');
  };

  const handleConfirm = async () => {
    if (!modalState.prediction || confirming) return;
    setConfirming(true);

    switch (modalState.type) {
      case 'accept': {
        const selectedPred = modalState.prediction.predictions[modalState.fromIndex];
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          'VERIFIED',
          selectedPred.typeid,
          selectedPred.lengthid,
          `Prediction accepted.`
        );
        break;
      }
      case 'custom-change': {
        const fromPred = modalState.prediction.predictions[modalState.fromIndex];
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          'VERIFIED',
          customValues.typeid,
          customValues.lengthid,
          `Prediction changed to custom values: Type: ${customValues.typename}, Length: ${customValues.length}mm`
        );
        break;
      }
      case 'change': {
        const fromPred = modalState.prediction.predictions[modalState.fromIndex];
        const toPred = modalState.prediction.predictions[modalState.toIndex];
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          'VERIFIED',
          toPred.typeid,
          toPred.lengthid,
          `Changed prediction to ${toPred.typename}. Length: ${toPred.length}mm`
        );
        break;
      }
      case 'error': {
        const errorMsg = selectedError === 'other' ?
          customError :
          errorOptions.find(opt => opt.id === selectedError)?.mail_message_nl + ' ||| ' +
          errorOptions.find(opt => opt.id === selectedError)?.mail_message_fr + ' ||| ' +
          errorOptions.find(opt => opt.id === selectedError)?.mail_message_en + ' ||| ' +
          errorOptions.find(opt => opt.id === selectedError)?.mail_message_de;
        await handleUpdatePrediction(
          modalState.prediction.clientuploadid,
          'FAILED',
          undefined,
          undefined,
          `${errorMsg}`
        );
        break;
      }
    }

    setConfirming(false);
    handleClose();
  };

  const handleClose = () => {
    setModalState({
      isOpen: false,
      type: null,
      prediction: null,
      fromIndex: 0,
      toIndex: 0
    });
    setSelectedError('');
    setCustomError('');
    setConfirming(false);
  };

  const CustomAlert = ({ message }: { message: string }) => (
    <div className="alert-message">
      <p>{message}</p>
    </div>
  );

  return (
    <div>
      <div className="dashboard-header">
        <h1>Evaluate Locators</h1>
        <div className="dashboard-controls">
          <label className="filter-control">
            <input
              type="checkbox"
              checked={showUnverifiedOnly}
              onChange={(e) => setShowUnverifiedOnly(e.target.checked)}
            />
            Show unverified only
          </label>
          <button className="btn refresh-button" onClick={fetchPredictions}>
            Refresh
          </button>
        </div>
      </div>

      {error && <CustomAlert message={error} />}

      {loading ? (
        <div className="loading-message">Loading predictions...</div>
      ) : (
        <div className="predictions-list">
          {filteredPredictions.map((prediction) => (
            <PredictionCard
              key={prediction.clientuploadid}
              prediction={prediction}
              onAccept={handleAcceptClick}
              onCustomChange={handleCustomChangeClick}
              onChange={handleChangeClick}
              onError={handleErrorClick}
            />
          ))}
        </div>
      )}

      <Modal isOpen={modalState.isOpen} onClose={handleClose}>
        {modalState.type === 'accept' && modalState.prediction && (
          <>
            <div className="modal-header">
              <h2>Confirm Accept</h2>
              <p>Are you sure you want to accept this prediction?</p>
              <div className="image-container">
                <img src={modalState.prediction.path} alt="Part" />
              </div>
              <p className="selected-prediction">
                <b>Type:  </b> {modalState.prediction.predictions[modalState.fromIndex].typename} <br />
                <b>Length:</b> {modalState.prediction.predictions[modalState.fromIndex].length} mm
              </p>
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button className="accept" onClick={handleConfirm} disabled={confirming}>
                {confirming ? "Accepting..." : "Yes, Accept"}
              </button>
            </div>
          </>
        )}
        {modalState.type === 'custom-change' && modalState.prediction && (
          <>
            <div className="modal-header">
              <h2>Choose values</h2>
              <p>Enter values for this prediction:</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction.path} alt="Part" />
              </div>
              <LocatorDropdownProps
              onChooseLocatorLength={(evt: any) => {
                setCustomValues({
                ...customValues,
                lengthid: evt.target.value,
                length: parseFloat(evt.target.name)
                });
              }}
              onChooseLocatorType={(evt: any) => {
                setCustomValues({
                ...customValues,
                typeid: evt.target.value,
                typename: evt.target.name
                });
              }}
              selectedLocatorType={customValues.typeid}
              selectedLocatorLength={customValues.lengthid}
              />
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button
                className="custom-change"
                onClick={handleConfirm}
                disabled={!customValues.typeid || !customValues.lengthid || confirming}
              >
                {confirming ? "Saving..." : "Confirm Change"}
              </button>
            </div>
          </>
        )}

        {modalState.type === 'error' && (
          <>
            <div className="modal-header">
              <h2>Report Error</h2>
              <p>What went wrong?</p>
            </div>
            <div className="modal-body">
              <div className="image-container">
                <img src={modalState.prediction?.path} alt="Part" />
              </div>
              <div className="error-options">
                <div className="radio-group">
                  {errorOptions.map((option) => (
                    <div key={option.id} className="radio-option">
                      <input
                        type="radio"
                        id={option.id}
                        checked={selectedError === option.id}
                        onChange={() => setSelectedError(option.id)}
                      />
                      <label htmlFor={option.id}>{option.label}</label>
                    </div>
                  ))}
                </div>
                {selectedError === 'other' && (
                  <input
                    type="text"
                    placeholder="Please specify the error"
                    value={customError}
                    className="custom-error-input"
                    onChange={(e) => setCustomError(e.target.value)}
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button
                className="error"
                onClick={handleConfirm}
                disabled={!selectedError || (selectedError === 'other' && !customError) || confirming}
              >
                {confirming ? "Submitting..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default EvaluateLocators;